import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {Store} from '@ngrx/store';
import {ChallengeSalvage, ChallengeTarget} from 'src/app/core/model/project';
import {AddSalvageAction, UpdateSalvageAction} from 'src/app/core/store/challenge-builder/challenge-builder.action';
import {selectChallengeBuilderProject} from 'src/app/core/store/challenge-builder/challenge-builder.selector';
import {MessageService} from 'src/app/services/message.service';
import {CbDialogFooterComponent, CbDialogHeaderComponent} from '../cb-dialog-parts.component';
import {OphXButtonComponent} from 'src/app/shared/design/oph-x-button/oph-x-button.component';

interface DialogData {
  salvage?: ChallengeSalvage;
  targets?: ChallengeTarget[];
}

@Component({
  selector: 'cb-salvage-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatMenuModule,
    MatIconModule,
    CbDialogHeaderComponent,
    CbDialogFooterComponent,
    OphXButtonComponent,
  ],
  templateUrl: './cb-salvage-dialog.component.html',
  styleUrl: './cb-salvage-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CbSalvageDialogComponent {
  private store$ = inject(Store);
  private messageService = inject(MessageService);
  readonly dialogRef = inject(MatDialogRef<CbSalvageDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA, {optional: true});

  project = this.store$.selectSignal(selectChallengeBuilderProject);

  salvageName = signal(this.data?.salvage?.name || '');
  loading = signal(false);

  targets = this.data?.targets || [];

  selectedTargetId = signal(this.resolveInitialTargetId());

  private resolveInitialTargetId(): string {
    const targetId = this.data?.salvage?.targetId;
    if (!targetId) return '';
    return targetId;
  }

  isEditMode = computed(() => !!this.data?.salvage?._id);
  titleVariable = computed(() => (this.isEditMode() ? 'Edit a' : 'New'));

  selectedTargetName = computed(() => {
    const targetId = this.selectedTargetId();
    if (!targetId) return 'Select target';
    const target = this.targets.find(t => t._id === targetId);
    return target?.targetName || 'Select target';
  });

  saveDisabled = computed(() => !this.salvageName().trim() || !this.selectedTargetId());

  onTargetSelect(target: ChallengeTarget): void {
    this.selectedTargetId.set(target._id || '');
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    const projectId = this.project()?._id;
    const name = this.salvageName().trim();
    const targetId = this.selectedTargetId();

    if (!projectId || !name || !targetId) {
      return;
    }

    const salvage: ChallengeSalvage = {
      ...(this.data?.salvage?._id ? {_id: this.data.salvage._id} : {}),
      name,
      targetId,
    };

    this.loading.set(true);

    if (this.isEditMode()) {
      this.store$.dispatch(
        new UpdateSalvageAction({
          projectId,
          salvage,
          onSuccess: () => this.onSaveSuccess(),
          onFailure: error => this.onSaveFailure(error),
        })
      );
    } else {
      this.store$.dispatch(
        new AddSalvageAction({
          projectId,
          salvage,
          onSuccess: () => this.onSaveSuccess(),
          onFailure: error => this.onSaveFailure(error),
        })
      );
    }
  }

  private onSaveSuccess(): void {
    this.loading.set(false);
    this.dialogRef.close(true);
  }

  private onSaveFailure(error: Error): void {
    this.loading.set(false);
    this.messageService.add(error.message || 'Failed to add salvage', 'error');
  }
}
